import React, { useMemo } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { CroppedImageView } from '@bingads-webui-campaign-react/cropped-image-view';
import type { UAPPlacementComponentProps } from '../../@types';

import {
  getResponsiveBingSearchTextPlacementStyles,
} from '../../styles';
import { withUAPLThemedStyles } from '../../with-themed-styles';

import {
  AdLabel,
  PlacementHeadline,
  PlacementFinalUrl,
  PlacementDescription,
} from '../../shared-components';

const BingSearchPlacementBase = ({
  // UAPVMSingleImagePlacement
  placementData,

  showShortHeadline,
  shortHeadline,
  isMultiLineShortHeadline,
  showLongHeadline,
  longHeadline,
  isMultiLineLongHeadline,
  showFinalUrl,
  showLogo,
  logo,
  finalUrl,
  isMultiLineFinalUrl,
  displayUrl,
  description,
  businessName,
  isMultiLineDescription,

  // may need to set window._TL_ in other platforms
  i18n,

  classes,
  customClassName,
  children,
  adExtensionContainerClassName,
}: UAPPlacementComponentProps) => (
  <div className={classnames(classes.placementCard, 'ad-placement-card-preview', customClassName)}>
    <div className="card-parent">
      <a
        className="placement-card-content"
        target="_blank"
        rel="noopener noreferrer"
        href={finalUrl || displayUrl}
      >
        <div className={classnames(classes.placementTextContainer, 'text-container')}>
          {showLogo && logo &&
          <div className="logo-and-text-container">
            <div className={classnames(classes.placementLogoContainer, 'logo-container')}>
              <CroppedImageView
                resizeImageHeight={(placementData && placementData.placementMeta && placementData.placementMeta.logoHeight) || 24}
                src={logo.Src}
                originalWidth={logo.Width}
                originalHeight={logo.Height}
                SourceX={logo.CropSettings.SourceX}
                SourceY={logo.CropSettings.SourceY}
                SourceWidth={logo.CropSettings.SourceWidth}
                SourceHeight={logo.CropSettings.SourceHeight}
                aspectRatio={1}
              />
            </div>
            <div className="logo-text">
              <div className="logo-business-name">{businessName}</div>
              <PlacementFinalUrl

                finalUrl={finalUrl}
                displayUrl={displayUrl}
                isMultiLine
                placementFinalUrlClassName={classnames(classes.placementFinalUrl, 'final-url')}
              />
            </div>
          </div>
        }
          {showShortHeadline && (
            <PlacementHeadline

              headlineText={shortHeadline}
              isMultiLine={isMultiLineShortHeadline}
              placementHeadlineClassName={classnames(classes.placementShortHeadline, 'short-headline')}
            />
          )}
          {showLongHeadline && (
            <PlacementHeadline

              headlineText={longHeadline}
              isMultiLine={isMultiLineLongHeadline}
              placementHeadlineClassName={classnames(classes.placementLongHeadline, 'long-headline')}
            />
          )}
          {(!showLogo || !logo) && showFinalUrl && (
            <PlacementFinalUrl

              finalUrl={finalUrl}
              displayUrl={displayUrl}
              isMultiLine={isMultiLineFinalUrl}
              placementFinalUrlClassName={classnames(classes.placementFinalUrl, 'final-url')}
            />
          )}
          <div className={classnames(classes.placementDescriptionContainer, 'business-name-with-ads')}>
            <AdLabel i18n={i18n} adsLabelClassName={classes.placementAdsLabel} />
            <PlacementDescription

              description={description}
              isMultiLine={isMultiLineDescription}
              placementDescriptionClassName={classes.placementDescription}
            />
          </div>
        </div>
        {children && (
        <div className={classnames(adExtensionContainerClassName, classes.adExtensionContainer)}>
          {children}
        </div>
      )}
      </a>
    </div>
  </div>
);

BingSearchPlacementBase.propTypes = {
  showShortHeadline: PropTypes.bool,
  shortHeadline: PropTypes.string,
  isMultiLineShortHeadline: PropTypes.bool,

  showLongHeadline: PropTypes.bool,
  longHeadline: PropTypes.string,
  isMultiLineLongHeadline: PropTypes.bool,

  showFinalUrl: PropTypes.bool,
  finalUrl: PropTypes.string,
  isMultiLineFinalUrl: PropTypes.bool,
  displayUrl: PropTypes.string,

  description: PropTypes.string,
  isMultiLineDescription: PropTypes.bool,

  businessName: PropTypes.string,
  placementData: PropTypes.shape({}), // UAPVMSingleImagePlacement
  showLogo: PropTypes.bool,
  logo: PropTypes.shape({
    Src: PropTypes.string,
    Width: PropTypes.number,
    Height: PropTypes.number,
    CropSettings: PropTypes.shape({
      SourceX: PropTypes.number,
      SourceY: PropTypes.number,
      SourceWidth: PropTypes.number,
      SourceHeight: PropTypes.number,
    }),
  }),

  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  classes: PropTypes.shape({
    placementCard: PropTypes.string,
    placementTextContainer: PropTypes.string,
    placementAdsLabel: PropTypes.string,
    placementShortHeadline: PropTypes.string,
    placementLongHeadline: PropTypes.string,
    placementFinalUrl: PropTypes.string,
    placementDescriptionContainer: PropTypes.string,
    placementDescription: PropTypes.string,
    placementLogoContainer: PropTypes.string,
    adExtensionContainer: PropTypes.string,
  }).isRequired,
  customClassName: PropTypes.string,
};

BingSearchPlacementBase.defaultProps = {
  showShortHeadline: false,
  showLongHeadline: false,
  showFinalUrl: false,

  shortHeadline: undefined,
  isMultiLineShortHeadline: false,
  longHeadline: undefined,
  isMultiLineLongHeadline: false,
  finalUrl: undefined,
  displayUrl: undefined,
  isMultiLineFinalUrl: false,
  description: undefined,
  isMultiLineDescription: false,
  customClassName: undefined,
  businessName: undefined,
  showLogo: false,
  logo: undefined,
  placementData: {},
};

export const ResponsiveAdPlacementBingSearchText = withUAPLThemedStyles(

  (props: UAPPlacementComponentProps) => {
    const { allShortHeadlines } = props;

    const combinedShortHeadlines = useMemo(() => {
      if (allShortHeadlines) {
        const headlinesArray = _.values(allShortHeadlines).splice(0, 3);
        const cleanedHeadlinesArray = _.without(headlinesArray, '');

        return cleanedHeadlinesArray.join(' | ');
      }

      return null;
    }, [allShortHeadlines]);

    return (
      <BingSearchPlacementBase
        {...props}
        longHeadline={combinedShortHeadlines || props.longHeadline}
        showShortHeadline={false}
        showLongHeadline
        showFinalUrl
        isMultiLineShortHeadline
        isMultiLineDescription
        isMultiLineFinalUrl
      />
    );
  },
  getResponsiveBingSearchTextPlacementStyles,
  { componentName: 'AdPlacementBingSearchText' }
);
